'use client';

import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';
import type { ScopedMutator } from 'swr';
import { ApiError } from '@/lib/api';
import { handleApiError } from '@/lib/upgradeRequired';

export function useAuthedFetcher() {
  const { getToken } = useAuth();

  return useCallback(
    async <T>(apiCall: (token: string) => Promise<T>): Promise<T> => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      try {
        return await callWithNetworkRetry(() => apiCall(token));
      } catch (err) {
        if (err instanceof ApiError && err.isAuthError()) {
          const fresh = await getToken({ skipCache: true });
          if (!fresh) throw new Error('Not authenticated');
          try {
            return await callWithNetworkRetry(() => apiCall(fresh));
          } catch (freshErr) {
            handleApiError(freshErr);
            throw freshErr;
          }
        }
        // Surface 402 limit-exceeded as a global upgrade dialog. Callers still get the
        // thrown ApiError so they can suppress redundant toasts via err.isLimitExceeded().
        handleApiError(err);
        throw err;
      }
    },
    [getToken],
  );
}

// Browser fetch throws `TypeError: Failed to fetch` on TCP resets / transient network
// blips. Retry once with a small delay so a single Kestrel RST doesn't surface as a
// user-visible error toast.
async function callWithNetworkRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof TypeError) {
      await new Promise((resolve) => setTimeout(resolve, 250));
      return fn();
    }
    throw err;
  }
}

export const LONG_LIVED_SWR_OPTIONS = {
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  dedupingInterval: 30_000,
  focusThrottleInterval: 15_000,
  keepPreviousData: true,
} as const;

export const VOLATILE_SWR_OPTIONS = {
  revalidateOnMount: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  dedupingInterval: 0,
  focusThrottleInterval: 0,
  keepPreviousData: false,
} as const;

export const TRANSACTION_SWR_OPTIONS = {
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  dedupingInterval: 15_000,
  focusThrottleInterval: 10_000,
  keepPreviousData: true,
} as const;

export const CREDIT_COCKPIT_KEY_PREFIXES = [
  'credit-cockpit',
] as const;

export const RECURRING_PAYMENT_KEY_PREFIXES = [
  'recurring-payments',
] as const;

export const FINANCIAL_DATA_KEY_PREFIXES = [
  'accounts',
  'categories',
  'transactions',
  'transaction-details',
  'recurring-payments',
  'review-queue',
  'home-widget',
  ...CREDIT_COCKPIT_KEY_PREFIXES,
] as const;

export function swrKeyStartsWith(key: unknown, prefixes: readonly string[]) {
  const first = Array.isArray(key) ? key[0] : key;
  return typeof first === 'string'
    && prefixes.some((prefix) => first === prefix || first.startsWith(`${prefix}-`));
}

/** The goals overview and the Home cards, which core.api refreshes on every goal write (its `WriteEffect.HomeWidgets`). */
export const GOAL_DATA_KEY_PREFIXES = ['goals-overview', 'home-widget'] as const;

export function revalidateGoalCaches(mutateGlobal: ScopedMutator) {
  void mutateGlobal((key) => swrKeyStartsWith(key, GOAL_DATA_KEY_PREFIXES));
}

export const TRANSACTION_DATA_KEY_PREFIXES = [
  'transactions',
  'transaction-details',
] as const;

/**
 * Revalidates every SWR cache under `prefixes` AND resets the `useSWRInfinite`
 * transactions lists.
 *
 * A filter-based `mutate` alone can never refresh an infinite list: SWR skips the
 * `$inf$` meta keys during filter iteration, and on remount the infinite sweep reuses
 * the per-page caches (`revalidateFirstPage: false`), so a list that isn't mounted
 * when the data changes stays stale until a full page reload. Dropping the per-page
 * entries (revalidate: false — nothing subscribes to page keys directly) makes the
 * next sweep refetch every page while the assembled list keeps rendering the previous
 * rows — stale-while-revalidate, no loading flash.
 */
export function revalidateTransactionCaches(
  mutateGlobal: ScopedMutator,
  prefixes: readonly string[] = TRANSACTION_DATA_KEY_PREFIXES,
) {
  void mutateGlobal((key) => swrKeyStartsWith(key, prefixes));
  void mutateGlobal(
    (key) => Array.isArray(key) && key[0] === 'transactions-infinite',
    undefined,
    { revalidate: false },
  );
  // Whatever changed the transactions (a category from the review queue, a rename, a sync) may have changed what
  // the person's sending rules sent.
  revalidateSpaceSending(mutateGlobal);
}

/** Every read that says what is in the person's spaces, or what their rules would send there. The rows' own markers
 *  travel with the transactions (`Transaction.spaceExpenses`, VER-571), under the transaction keys. */
export const SPACE_SENDING_KEYS = ['space-send-preview', 'space-expenses', 'space-home', 'space-rules-overview'] as const;

export function isSpaceSendingKey(key: unknown, userId?: string | null): boolean {
  return Array.isArray(key) && (SPACE_SENDING_KEYS as readonly string[]).includes(String(key[0])) && (!userId || key[1] === userId);
}

/**
 * Reads the space markers again. Tagging or recategorising a purchase can send it to a space: the API evaluates the
 * person's sending rules on the edit (VER-444), so a row's "Nossa Casa" marker has to appear right after the save
 * and not at the next reload. A filter mutate only touches keys that exist, and these only exist while the
 * shared-spaces flag is on: with the flag off this requests nothing.
 */
export function revalidateSpaceSending(mutateGlobal: ScopedMutator, userId?: string | null) {
  void Promise.resolve(mutateGlobal((key) => isSpaceSendingKey(key, userId))).catch(() => undefined);
}

/**
 * Revalidates every review-queue cache — the queue is keyed per kind
 * (['review-queue', 'all' | 'category' | 'refund']), and resolving an item on one
 * surface must refresh the others (home total, /budget categories, /transactions
 * refunds), so invalidation always sweeps the whole prefix.
 */
export function revalidateReviewQueues(mutateGlobal: ScopedMutator) {
  void mutateGlobal((key) => swrKeyStartsWith(key, ['review-queue']));
}

/**
 * Revalidates the `['custom-categories']` SWR cache (useCustomCategories). Call this from
 * every custom-category create/rename/archive/unarchive mutation — CategoryPickerDrawer
 * reads customs exclusively from this key, not from category-overview rows, so any handler
 * that only refreshes the overview leaves it stale within SWR's dedupe window.
 */
export function revalidateCustomCategories(mutateGlobal: ScopedMutator) {
  void mutateGlobal((key) => Array.isArray(key) && key[0] === 'custom-categories');
}

/**
 * Revalidates the `['tags']` SWR cache (useTags). Call from every tag create/delete —
 * the assign and filter drawers read the catalog exclusively from this key.
 */
export function revalidateTagCatalog(mutateGlobal: ScopedMutator) {
  void mutateGlobal((key) => Array.isArray(key) && key[0] === 'tags');
}
